/**
 * Settings — Connected apps.
 *
 * The signed-in user's LLM connector sessions: which AI clients they have allowed to act as
 * them, with what permissions, and a Disconnect button. A disconnect takes effect on the
 * session's very next call — the server mirrors it into a revocation set the MCP server checks
 * per request — so the person never has to wait for a token to expire.
 *
 * Route: /settings/connected-apps
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconPlugConnected, IconPlugConnectedX, IconRefresh, IconRobot } from '@tabler/icons-react';
import { Stack, Group, Button, Text, Title, Paper, Badge, Alert, Loader, Code, modal } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { EmptyState } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import MainLayout from '@/layouts/MainLayout';
import { useConnectorService } from '@actschurch/ccms-sdk';
import type { ConnectorGrant } from '@actschurch/ccms-sdk';
import { formatRelative, formatStamp, isWriteScope, scopeLabel, scopeList } from './connectorFormat';

const MY_GRANTS_KEY = ['connector', 'my-grants'];

export default function ConnectedAppsPage() {
  const service = useConnectorService();
  const queryClient = useQueryClient();

  const { data: grants = [], isLoading, isFetching, error } = useQuery({
    queryKey: MY_GRANTS_KEY,
    queryFn: () => service.getMyGrants(),
  });

  const live = grants.filter((g) => g.isLive);
  const past = grants.filter((g) => !g.isLive);

  const refresh = () => queryClient.invalidateQueries({ queryKey: MY_GRANTS_KEY });

  const disconnect = (grant: ConnectorGrant) => {
    modal.confirm({
      title: 'Disconnect this app?',
      body:
        'It will lose access to CCMS on its next request. You can connect it again later from the app itself.',
      confirmLabel: 'Disconnect',
      confirmColor: 'red',
      onConfirm: async () => {
        try {
          await service.revokeGrant(grant.grantID);
          toast.success('Disconnected', 'The app can no longer act on your behalf.');
          refresh();
        } catch (e) {
          toast.error('Could not disconnect', e instanceof Error ? e.message : 'Try again.');
        }
      },
    });
  };

  return (
    <MainLayout showSidebar={false}>
      <Stack gap="lg" maw={960} mx="auto">
        <FadeIn>
          <Paper py="md" px="lg" withBorder radius="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Group gap="xs">
                  <IconPlugConnected size={22} />
                  <Title order={2}>Connected apps</Title>
                </Group>
                <Text c="dimmed" size="sm" mt={4}>
                  AI assistants you have allowed to act as you in CCMS. Each one can only ever do what
                  your own account permits, and only within the permissions you approved.
                </Text>
              </div>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={refresh}
                loading={isFetching && !isLoading}
              >
                Refresh
              </Button>
            </Group>
          </Paper>
        </FadeIn>

        {error && (
          <Alert color="red" title="Could not load connected apps">
            {error instanceof Error ? error.message : 'Try again in a moment.'}
          </Alert>
        )}

        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : live.length === 0 ? (
          <FadeIn>
            <EmptyState
              icon={<IconRobot size={40} />}
              title="No connected apps"
              description="To connect Claude, add a custom connector in claude.ai pointing at the CCMS connector address, then sign in with your CCMS account. Your administrator must have enabled your account for connectors first."
              size="md"
            />
          </FadeIn>
        ) : (
          <Stack gap="md">
            {live.map((grant) => (
              <FadeIn key={grant.grantID}>
                <GrantCard grant={grant} onDisconnect={() => disconnect(grant)} />
              </FadeIn>
            ))}
          </Stack>
        )}

        {past.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="dimmed">
              Previously connected
            </Text>
            {past.map((grant) => (
              <GrantCard key={grant.grantID} grant={grant} />
            ))}
          </Stack>
        )}
      </Stack>
    </MainLayout>
  );
}

function GrantCard({ grant, onDisconnect }: { grant: ConnectorGrant; onDisconnect?: () => void }) {
  const scopes = scopeList(grant.grantedScopes);
  return (
    <Paper withBorder radius="md" p="md" opacity={grant.isLive ? 1 : 0.6}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6} style={{ flex: 1 }}>
          <Group gap="xs">
            <Text fw={600}>AI client</Text>
            <Code>{grant.clientId}</Code>
            {grant.isLive ? (
              <Badge color="green" variant="light">
                Connected
              </Badge>
            ) : (
              <Badge color="gray" variant="light">
                Disconnected {formatRelative(grant.revokedDateTime)}
              </Badge>
            )}
          </Group>
          <Group gap={6}>
            {scopes.map((scope) => (
              <Badge key={scope} variant="outline" color={isWriteScope(scope) ? 'orange' : 'blue'} size="sm">
                {scopeLabel(scope)}
              </Badge>
            ))}
          </Group>
          <Text size="xs" c="dimmed">
            Connected {formatStamp(grant.createdDateTime)} · Last used {formatRelative(grant.lastUsedDateTime)}
          </Text>
        </Stack>
        {grant.isLive && onDisconnect && (
          <Button
            variant="light"
            color="red"
            leftSection={<IconPlugConnectedX size={16} />}
            onClick={onDisconnect}
          >
            Disconnect
          </Button>
        )}
      </Group>
    </Paper>
  );
}
